'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';

const quickLinks = [
  { label: 'About Us', href: '#about' },
  { label: 'Products', href: '#products' },
  { label: 'Conference Kits', href: '#conference-kits' },
  { label: 'Custom Packaging', href: '#custom-packaging' },
  { label: 'Contact Us', href: '#contact' },
  { label: 'FAQs', href: '#' },
];

const productLinks = [
  { label: 'Ecom Envelopes', href: '#ecom-packaging' },
  { label: 'Ecom Boxes', href: '#ecom-packaging' },
  { label: 'Cotton Bags', href: '#apparel-packaging' },
  { label: 'Glass Jars', href: '#ecom-packaging' },
  { label: 'Seed Paper Products', href: '#conference-kits' },
  { label: 'Kraft Folders', href: '#conference-kits' },
];

export default function Footer() {
  const [year, setYear] = useState(2025);

  useEffect(() => {
    setYear(new Date()?.getFullYear());
  }, []);

  return (
    <footer className="bg-primary-dark text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 pb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <Image
                src="/assets/images/Group_464-1786683986985.png"
                alt="Ecosange - Sustainable Packaging"
                width={130}
                height={44}
                className="h-10 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-white/60 text-sm font-normal leading-relaxed mb-4">
              Your eco-conscious packaging partner. From ecom boxes to conference kits — all crafted with the planet in mind.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/ecosange_packaging/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/ecosange/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks?.map((link) => (
                <li key={link?.label}>
                  <a
                    href={link?.href}
                    className="text-white/60 text-sm font-normal hover:text-white transition-colors duration-200"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Products</h3>
            <ul className="space-y-2">
              {productLinks?.map((link) => (
                <li key={link?.label}>
                  <a
                    href={link?.href}
                    className="text-white/60 text-sm font-normal hover:text-white transition-colors duration-200"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Stay Updated</h3>
            <p className="text-white/60 text-sm font-normal mb-3">
              Get packaging tips, eco insights, and new product launches.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 border border-white/20 rounded-xl px-3 py-2 text-sm font-normal text-white placeholder-white/30 focus:outline-none focus:border-white/50 bg-white/10"
              />
              <button className="bg-white text-primary-dark font-semibold text-sm px-4 py-2 rounded-xl hover:bg-gray-100 transition-colors">
                Go
              </button>
            </div>
            <div className="mt-4 p-3 bg-white/8 rounded-xl border border-white/10">
              <p className="text-xs font-semibold text-white/70">📍 TBS Workspace, 2nd Floor</p>
              <p className="text-xs text-white/40 font-normal mt-0.5">Patia, Bhubaneswar, Odisha – 751024</p>
              <p className="text-xs text-white/40 font-normal mt-1">📞 +91 91102 72914 &nbsp;|&nbsp; ✉️ info@ecosange.com</p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs font-normal">
            &copy; {year} Ecosange. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-white/40 text-xs font-normal hover:text-white/70 transition-colors">Privacy Policy</a>
            <a href="#" className="text-white/40 text-xs font-normal hover:text-white/70 transition-colors">Terms of Service</a>
            <a href="#" className="text-white/40 text-xs font-normal hover:text-white/70 transition-colors">Shipping Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
